import { useEffect, useRef, useState } from "react";
import { createVoicePipeline, silentAudio } from "./audio.ts";
import type { AudioPreferences, VoicePipeline } from "./audio.ts";
import { AudioMeter } from "./audio-meter.tsx";

type Test = { controller: AbortController; pipeline?: VoicePipeline; timer?: ReturnType<typeof setTimeout> };
export function AudioPreview({ preferences, disabled, onTesting }: { preferences: AudioPreferences; disabled: boolean; onTesting: (value: boolean) => void }) {
  const current = useRef<Test | null>(null);
  const notify = useRef(onTesting); notify.current = onTesting;
  const player = useRef<HTMLAudioElement>(null);
  const [testing, setTesting] = useState(false), [loading, setLoading] = useState(false), [listening, setListening] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [feedback, setFeedback] = useState({ ...silentAudio });
  const [error, setError] = useState("");
  function release() {
    const test = current.current; current.current = null;
    if (test?.timer) clearTimeout(test.timer);
    test?.controller.abort(); test?.pipeline?.close();
    if (player.current) { player.current.pause(); player.current.srcObject = null; }
  }
  function stop() { release(); setTesting(false); setLoading(false); setListening(false); setStream(null); setFeedback({ ...silentAudio }); notify.current(false); }
  useEffect(() => { if (disabled && current.current) stop(); }, [disabled]);
  useEffect(() => {
    const hidden = () => { if (document.hidden) stop(); };
    document.addEventListener("visibilitychange", hidden);
    return () => { document.removeEventListener("visibilitychange", hidden); release(); notify.current(false); };
  }, []);
  useEffect(() => {
    const element = player.current;
    if (!element) return;
    element.srcObject = listening ? stream : null; element.volume = 0.6;
    if (listening && stream) void element.play().catch(() => { setListening(false); setError("Não foi possível reproduzir o teste. Confira a saída de áudio e tente novamente."); });
    return () => { element.pause(); element.srcObject = null; };
  }, [stream, listening]);
  async function start() {
    if (disabled || current.current) return;
    setError(""); setLoading(true); setTesting(true); setListening(false); notify.current(true);
    const test: Test = { controller: new AbortController() }; current.current = test;
    try {
      const pipeline = await createVoicePipeline(preferences, value => {
        if (current.current !== test) return;
        if (value.mode === "off") { stop(); setError(value.warning || "O teste foi interrompido."); }
        else setFeedback(value);
      }, undefined, test.controller.signal);
      if (current.current !== test) { pipeline.close(); return; }
      test.pipeline = pipeline; setStream(pipeline.stream); setLoading(false);
      test.timer = setTimeout(stop, 20000);
    } catch (cause) {
      if (current.current !== test) return;
      stop();
      setError(cause instanceof Error && cause.name === "NotAllowedError" ? "Autorize o microfone no navegador para fazer o teste." : "Não foi possível testar este microfone. Confira o dispositivo selecionado e tente novamente.");
    }
  }
  return <section className="audio-preview" aria-label="Teste local do microfone">
    <div><strong>Teste antes de falar</strong><p className="muted">O teste não é transmitido para a sala e encerra após 20 segundos de áudio. Use fones para ouvir a própria voz.</p></div>
    <button disabled={!testing && disabled} onClick={() => testing ? stop() : void start()}>{testing ? "Encerrar teste" : "Testar microfone"}</button>
    {testing && <p role="status">{loading ? "Preparando microfone e filtros…" : "Teste local em andamento · sem transmissão"}</p>}
    {testing && !loading && <><AudioMeter feedback={feedback} /><label className="audio-listen"><input type="checkbox" checked={listening} onChange={event => setListening(event.target.checked)} />Ouvir minha voz nos fones</label></>}
    <audio ref={player} />
    {error && <p className="error" role="alert">{error}</p>}
  </section>;
}
